import { MAX_PRIMARY_ENTITY_SELECTIONS } from '@revelio/iso-utility';

import {
  metroGeographyBranchLabels,
  ricsAndCompanyBranchLabels,
  roleBranchLabels,
  skillsBranchLabels,
} from '../constants/branch-labels';
import { companyDefinition } from '../definitions/company';
import { getFilterDefinition } from '../engine/filterRegistry';
import {
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  type ListFilter,
  type PrimarySelection,
  type TreeFilter,
} from '../types/filters';
import {
  enforceMaxSelections,
  readPersistedPrimarySelections,
} from './primaryFilterUtils';

export const isValidId = (id: string) => /^[\d_]+$/.test(id);

/**
 * Move industry selections out of a secondary Company filter.
 *
 * Links, saved views and preferences predating the Company/Industry
 * split carry rics_* selections inside the Company filter. The primary
 * Company filter still accepts them, so only secondary ones move. Skipped on
 * Industry pages, where the Industry filter is the primary one and selections
 * must go through `primarySelections` and the selection cap.
 */
export const splitLegacyCompanyIndustrySelections = (
  newMap: FilterSelectionsMap,
  primaryFilterId?: FilterId
): void => {
  if (
    primaryFilterId === FilterId.COMPANY ||
    primaryFilterId === FilterId.INDUSTRY
  ) {
    return;
  }

  const companyToSplit = newMap.get(FilterId.COMPANY);
  if (companyToSplit?.type !== FilterType.TREE || companyToSplit.isPrimary) {
    return;
  }

  const industrySelections = (companyToSplit.selections ?? []).filter(
    (s) => s.levelId !== 'company'
  );
  if (!industrySelections.length) return;

  // Saved sets serialize only the filters that were set, so the Industry base
  // may be absent from the map — fall back to its definition.
  const industryBase = (() => {
    const fromMap = newMap.get(FilterId.INDUSTRY);
    if (fromMap !== undefined) {
      return fromMap.type === FilterType.TREE && !fromMap.isPrimary
        ? fromMap
        : undefined;
    }
    const definition = getFilterDefinition(FilterId.INDUSTRY);
    return definition?.type === FilterType.TREE
      ? ({ ...definition } as TreeFilter)
      : undefined;
  })();
  if (!industryBase) return;

  newMap.set(FilterId.COMPANY, {
    ...companyToSplit,
    // The persisted filter also carries its pre-split "Company & Industry"
    // name; refresh it along with the selections it no longer holds.
    name: companyDefinition.name,
    selections: (companyToSplit.selections ?? []).filter(
      (s) => s.levelId === 'company'
    ),
  });

  const alreadySelected = new Set(
    (industryBase.selections ?? []).map((s) => s.id)
  );
  newMap.set(FilterId.INDUSTRY, {
    ...industryBase,
    selections: [
      ...(industryBase.selections ?? []),
      ...industrySelections.filter((s) => !alreadySelected.has(s.id)),
    ],
  });
};

export const validateParsedFilters = (
  parsedFilters: unknown,
  {
    defaultFilterMap,
    primaryFilterId,
  }: {
    defaultFilterMap: FilterSelectionsMap;
    primaryFilterId?: FilterId;
  }
) => {
  const newMap: FilterSelectionsMap = new Map(defaultFilterMap);

  // If the parsed object is invalid, bail early
  if (!parsedFilters || !Array.isArray(parsedFilters)) {
    console.error('Invalid filters object', parsedFilters);
    return newMap;
  }

  parsedFilters.forEach((parsedFilter) => {
    if (!Array.isArray(parsedFilter)) return;

    const [id, value] = parsedFilter;
    if (!id || !value) return;

    // -----------------------
    // Primary filter
    // -----------------------
    if (id === primaryFilterId) {
      const existingPrimaryFilter = newMap.get(id) as TreeFilter;
      const validatedPrimarySelections = readPersistedPrimarySelections(
        value?.primarySelections ?? value?.selections
      ).filter((s: PrimarySelection) => isValidId(s.id));

      // Seed from the default map (capped) so an unusable payload falls back to
      // it. Guarded: additional deeplink groups are validated against an empty
      // default map, and writing a bare selections pair there would replace the
      // group's filter with a shapeless object.
      if (existingPrimaryFilter) {
        const existingSelections = enforceMaxSelections(
          existingPrimaryFilter.primarySelections || [],
          MAX_PRIMARY_ENTITY_SELECTIONS
        );
        newMap.set(id, {
          ...existingPrimaryFilter,
          primarySelections: existingSelections,
          selections: existingSelections,
        });
      }

      if (value.type !== FilterType.TREE) return;

      // If no valid selections, keep default filter map
      if (!validatedPrimarySelections.length) return;

      const cappedSelections = enforceMaxSelections(
        validatedPrimarySelections,
        MAX_PRIMARY_ENTITY_SELECTIONS
      );

      newMap.set(id, {
        ...value,
        primarySelections: cappedSelections,
        selections: cappedSelections,
      } as TreeFilter);
      return;
    }

    // -----------------------
    // Generic ID validation for all filter types with selections
    // -----------------------
    if (value?.type === FilterType.TREE && value?.selections) {
      // Tree filters (already handled above for specific types, but ensure all are covered)
      const validatedSelections = value.selections.filter(
        (s: TreeFilter['selections'][number]) => isValidId(s.id)
      );
      // Primary tree filters of non-active groups don't match primaryFilterId
      // but still carry primarySelections — cap them here too so switching to
      // that group can't activate more than the global ceiling.
      const cappedPrimarySelections = value.isPrimary
        ? {
            primarySelections: enforceMaxSelections(
              readPersistedPrimarySelections(value.primarySelections).filter(
                (s: PrimarySelection) => isValidId(s.id)
              ),
              MAX_PRIMARY_ENTITY_SELECTIONS
            ),
          }
        : {};
      newMap.set(id, {
        ...value,
        selections: validatedSelections,
        ...cappedPrimarySelections,
      });
      return;
    }

    if (value?.type === FilterType.LIST && value?.selections) {
      // List filters (GENDER, ETHNICITY, EDUCATION, SENIORITY, etc.)
      const validatedSelections = value.selections.filter(
        (s: ListFilter['selections'][number]) => isValidId(s.id)
      );
      newMap.set(id, {
        ...value,
        selections: validatedSelections,
      });
      return;
    }

    if (value?.type === FilterType.STATIC_LIST && value?.selections) {
      // Static list filters
      newMap.set(id, {
        ...value,
        selections: value.selections,
      });
      return;
    }

    newMap.set(id, value);
  });

  splitLegacyCompanyIndustrySelections(newMap, primaryFilterId);

  // ---------------------------------------------------------------------------
  // Reinstate branch labels that could be lost via URL state
  // ---------------------------------------------------------------------------
  const companyFilter = newMap.get(FilterId.COMPANY) as TreeFilter;
  if (companyFilter) {
    newMap.set(FilterId.COMPANY, {
      ...companyFilter,
      name: companyDefinition.name,
      branches: ricsAndCompanyBranchLabels,
    });
  }

  const industryFilter = newMap.get(FilterId.INDUSTRY) as TreeFilter;
  if (industryFilter) {
    newMap.set(FilterId.INDUSTRY, {
      ...industryFilter,
      branches: ricsAndCompanyBranchLabels,
    });
  }

  const geographyFilter = newMap.get(FilterId.GEOGRAPHY) as TreeFilter;
  if (geographyFilter) {
    newMap.set(FilterId.GEOGRAPHY, {
      ...geographyFilter,
      branches: metroGeographyBranchLabels,
    });
  }

  const roleFilter = newMap.get(FilterId.ROLE) as TreeFilter;
  if (roleFilter) {
    newMap.set(FilterId.ROLE, {
      ...roleFilter,
      branches: roleBranchLabels,
    });
  }

  const skillFilter = newMap.get(FilterId.SKILL) as TreeFilter;
  if (skillFilter) {
    newMap.set(FilterId.SKILL, {
      ...skillFilter,
      branches: skillsBranchLabels,
    });
  }

  return newMap;
};
